/**
 * Dashboard — operational overview.
 *
 * Single-page, polls every 15s. Sections:
 *
 *   1. KPI strip (workflows, runs 24h, failed, buckets, webhook endpoints, templates)
 *   2. Stale-buckets banner (only if there are any)
 *   3. Runs-per-hour stacked bar chart with 24h/7d range selector
 *      — clicking a bar deep-links to /workflow-runs filtered by hour
 *   4. Three columns: Now (running) | Recent activity | Top failing
 *   5. Latency leaderboard (avg / p95 / max per workflow)
 *
 * Every secondary data point is its own query so a slow latency
 * computation doesn't block the KPI strip render.
 */
import { useQuery } from "@tanstack/react-query";
import { Activity, CheckCircle2, Send } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { AttentionStrip, type AttentionItem } from "@/components/dashboard/AttentionStrip";
import {
  AiCard,
  AppsCard,
  DataCard,
  EtlCard,
  GatewaysCard,
  IntegrationCard,
  OpsCard,
} from "@/components/dashboard/PlaneCards";
import { RunsTimeseriesChart } from "@/components/dashboard/RunsTimeseriesChart";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { dashboardApi, type ActivityItem } from "@/lib/api-dashboard";
import { usePermissions } from "@/lib/permissions";

function statusBadgeTone(s: string): "active" | "danger" | "warning" | "neutral" {
  if (s === "succeeded") return "active";
  if (s === "failed") return "danger";
  if (s === "running") return "warning";
  return "neutral";
}

function fmtDuration(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function fmtRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function ActivityRow({ a }: { a: ActivityItem }) {
  return (
    <Link
      to={`/workflow-runs?execution_id=${a.execution_id}`}
      className="flex items-center justify-between border-b border-border py-1.5 text-xs last:border-b-0 hover:bg-accent/5"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Badge tone={statusBadgeTone(a.status)}>{a.status}</Badge>
        <span className="truncate font-medium">{a.workflow_name}</span>
        <span className="text-xs text-muted-foreground">{a.trigger}</span>
      </div>
      <div className="ml-2 shrink-0 text-right">
        <div className="font-mono text-xs text-muted-foreground">{fmtDuration(a.duration_ms)}</div>
        <div className="font-mono text-xs text-muted-foreground">
          {fmtRelativeTime(a.started_at)}
        </div>
      </div>
    </Link>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { can } = usePermissions();
  const [rangeHours, setRangeHours] = React.useState<24 | 168>(24);

  const overviewQuery = useQuery({
    queryKey: ["dashboard-overview"],
    queryFn: () => dashboardApi.overview(),
    refetchInterval: 15_000,
  });

  // Per-plane summary cards. Each query fires ONLY when the operator holds
  // the plane's permission (`enabled`), so a scoped operator never even
  // requests a card they can't see; the backend gate stays authoritative.
  const appsQuery = useQuery({
    queryKey: ["dashboard-apps-summary"],
    queryFn: () => dashboardApi.appsSummary(),
    enabled: can("apps", "read"),
    refetchInterval: 60_000,
  });
  const dataQuery = useQuery({
    queryKey: ["dashboard-data-summary"],
    queryFn: () => dashboardApi.dataSummary(),
    enabled: can("datasets", "read"),
    refetchInterval: 60_000,
  });
  const etlQuery = useQuery({
    queryKey: ["dashboard-etl-summary"],
    queryFn: () => dashboardApi.etlSummary(),
    enabled: can("etl", "read"),
    refetchInterval: 60_000,
  });
  const aiQuery = useQuery({
    queryKey: ["dashboard-ai-summary"],
    queryFn: () => dashboardApi.aiSummary(30),
    enabled: can("odino", "use"),
    refetchInterval: 60_000,
  });
  const opsQuery = useQuery({
    queryKey: ["dashboard-ops-summary"],
    queryFn: () => dashboardApi.opsSummary(),
    enabled: can("alerts", "read"),
    refetchInterval: 60_000,
  });
  const gatewaysQuery = useQuery({
    queryKey: ["dashboard-gateways-summary"],
    queryFn: () => dashboardApi.gatewaysSummary(),
    enabled: can("gateways", "read"),
    refetchInterval: 60_000,
  });
  const timeseriesQuery = useQuery({
    queryKey: ["dashboard-timeseries", rangeHours],
    queryFn: () => dashboardApi.runsTimeseries(rangeHours),
    refetchInterval: 30_000,
  });
  const activityQuery = useQuery({
    queryKey: ["dashboard-activity"],
    queryFn: () => dashboardApi.recentActivity(15),
    refetchInterval: 15_000,
  });
  const latencyQuery = useQuery({
    queryKey: ["dashboard-latency", rangeHours],
    queryFn: () => dashboardApi.latency(rangeHours, 10),
    refetchInterval: 60_000,
  });
  const staleQuery = useQuery({
    queryKey: ["dashboard-stale-buckets"],
    queryFn: () => dashboardApi.staleBuckets(60),
    refetchInterval: 60_000,
  });

  const d = overviewQuery.data;
  const runningRows = (activityQuery.data ?? []).filter((a) => a.status === "running");
  const rangeLabel = t(rangeHours === 24 ? "dashboard.chart.range24h" : "dashboard.chart.range7d");

  // "Needs attention" strip — derived from the plane summaries already in
  // cache (so it self-respects per-plane permissions) plus the integration
  // plane's own failure signals. Zero-count items are dropped by the strip.
  const attention: AttentionItem[] = [
    {
      key: "failed-runs",
      label: t("dashboard.attention.failedRuns", "run falliti (24h)"),
      count: d?.runs_24h.failed ?? 0,
      tone: "danger",
      href: "/workflow-runs?status=failed",
    },
    {
      key: "stale-buckets",
      label: t("dashboard.attention.staleBuckets", "bucket fermi"),
      count: staleQuery.data?.length ?? 0,
      tone: "warning",
      href: "/aggregate-buckets?status=collecting",
    },
    {
      key: "dead-letters",
      label: t("dashboard.attention.deadLetters", "consegne fallite"),
      count: opsQuery.data?.attention.dead_letters ?? 0,
      tone: "danger",
      href: "/deliveries",
    },
    {
      key: "open-alerts",
      label: t("dashboard.attention.openAlerts", "allerte (24h)"),
      count: opsQuery.data?.attention.open_alerts ?? 0,
      tone: "warning",
      href: "/alerts",
    },
    {
      key: "gateways-down",
      label: t("dashboard.attention.gatewaysDown", "gateway giù"),
      count: gatewaysQuery.data?.attention.down ?? 0,
      tone: "danger",
      href: "/gateways",
    },
    {
      key: "kb-failed",
      label: t("dashboard.attention.kbFailed", "ingest KB falliti"),
      count: dataQuery.data?.attention.kb_ingests_failed ?? 0,
      tone: "danger",
      href: "/knowledge-bases",
    },
    {
      key: "sources-down",
      label: t("dashboard.attention.sourcesDown", "sorgenti down"),
      count: dataQuery.data?.attention.sources_down ?? 0,
      tone: "danger",
      href: "/sources",
    },
    {
      key: "pending-registrations",
      label: t("dashboard.attention.pendingRegistrations", "registrazioni in attesa"),
      count: appsQuery.data?.attention.pending_registrations ?? 0,
      tone: "warning",
      href: "/portal-registrations",
    },
  ];

  return (
    <PageContainer>
      <PageHeader title={t("dashboard.title")} description={t("dashboard.subtitle")} />
      <PageContent>
        {/* Cross-plane "needs attention" strip (renders nothing when all clear) */}
        <AttentionStrip items={attention} />

        {/* The 4 load-bearing planes as QUADRANT panels — each with its own
            protagonist number + satellites + footer strip (sparkline / budget
            bar / warning). Workflow rides the overview (always present); the
            others gate on their plane permission, so a scoped operator only
            sees (and only fetches) the planes they're allowed. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <IntegrationCard overview={d} buckets={timeseriesQuery.data?.buckets} />
          {can("etl", "read") && <EtlCard data={etlQuery.data} />}
          {can("apps", "read") && <AppsCard data={appsQuery.data} />}
          {can("odino", "use") && <AiCard data={aiQuery.data} />}
        </div>

        {/* Secondary band — the supporting planes at a lower visual weight
            (Dati · Notifiche · Gateway). Each still gated + testid-stable. */}
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {can("datasets", "read") && <DataCard data={dataQuery.data} />}
          {can("alerts", "read") && <OpsCard data={opsQuery.data} />}
          {can("gateways", "read") && <GatewaysCard data={gatewaysQuery.data} />}
        </div>

        {/* Time-series chart */}
        <div className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>{t("dashboard.chart.title", { range: rangeLabel })}</CardTitle>
                <div className="inline-flex border border-border">
                  <button
                    type="button"
                    onClick={() => setRangeHours(24)}
                    className={
                      "px-2 py-1 text-xs " +
                      (rangeHours === 24
                        ? "bg-accent text-accent-foreground"
                        : "bg-surface text-muted-foreground hover:bg-accent/10")
                    }
                  >
                    {t("dashboard.chart.range24h")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRangeHours(168)}
                    className={
                      "px-2 py-1 text-xs " +
                      (rangeHours === 168
                        ? "bg-accent text-accent-foreground"
                        : "bg-surface text-muted-foreground hover:bg-accent/10")
                    }
                  >
                    {t("dashboard.chart.range7d")}
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {timeseriesQuery.data ? (
                <RunsTimeseriesChart
                  buckets={timeseriesQuery.data.buckets}
                  height={rangeHours === 24 ? 200 : 240}
                  onBucketClick={(b) => {
                    // Deep-link to /workflow-runs filtered by the hour
                    // window. /workflow-runs doesn't read those query
                    // params today (it filters by status / trigger),
                    // but linking lays the groundwork for it.
                    const hour = encodeURIComponent(b.hour);
                    nav(`/workflow-runs?from=${hour}`);
                  }}
                />
              ) : (
                <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                  {t("common.loading")}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Three columns: Running now | Recent activity | Top failing */}
        <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>{t("dashboard.runningNow")}</CardTitle>
            </CardHeader>
            <CardContent>
              {runningRows.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  {t("dashboard.noActive")}
                </div>
              ) : (
                <div>
                  {runningRows.slice(0, 8).map((a) => (
                    <ActivityRow key={a.execution_id} a={a} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("dashboard.recentActivity")}</CardTitle>
            </CardHeader>
            <CardContent>
              {!activityQuery.data ? (
                <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
              ) : activityQuery.data.length === 0 ? (
                <EmptyState
                  icon={Activity}
                  title={t("dashboard.noActivity")}
                  description={t("dashboard.triggerHint")}
                />
              ) : (
                <div>
                  {activityQuery.data.slice(0, 10).map((a) => (
                    <ActivityRow key={a.execution_id} a={a} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("dashboard.topFailing")}</CardTitle>
            </CardHeader>
            <CardContent>
              {!d ? (
                <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
              ) : d.top_failing_workflows_24h.length === 0 ? (
                <EmptyState
                  icon={CheckCircle2}
                  title={t("dashboard.allGreen")}
                  description={t("dashboard.noFailures")}
                />
              ) : (
                <div>
                  {d.top_failing_workflows_24h.map((tf) => (
                    <Link
                      key={tf.workflow_id}
                      to={`/workflow-runs?workflow_id=${tf.workflow_id}&status=failed`}
                      className="flex items-center justify-between border-b border-border py-2 text-sm last:border-b-0 hover:bg-accent/5"
                    >
                      <span className="truncate">{tf.workflow_name}</span>
                      <Badge tone="danger">{tf.failed_count}</Badge>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Latency leaderboard */}
        <div className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("dashboard.slowest", { range: rangeLabel })}</CardTitle>
            </CardHeader>
            <CardContent>
              {!latencyQuery.data ? (
                <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
              ) : latencyQuery.data.length === 0 ? (
                <EmptyState
                  icon={Send}
                  title={t("dashboard.noCompletedRuns")}
                  description={t("dashboard.noLatencyHint")}
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="py-1.5">{t("dashboard.table.workflow")}</th>
                        <th className="py-1.5 text-right">{t("dashboard.table.runs")}</th>
                        <th className="py-1.5 text-right">{t("dashboard.table.avg")}</th>
                        <th className="py-1.5 text-right">{t("dashboard.table.p95")}</th>
                        <th className="py-1.5 text-right">{t("dashboard.table.max")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latencyQuery.data.map((r) => (
                        <tr key={r.workflow_id} className="border-b border-border last:border-b-0">
                          <td className="py-1.5">
                            <Link to={`/workflows/${r.workflow_id}`} className="hover:text-accent">
                              {r.workflow_name}
                            </Link>
                            <span className="ml-2 text-xs text-muted-foreground">
                              {r.direction}
                            </span>
                          </td>
                          <td className="py-1.5 text-right font-mono">{r.run_count}</td>
                          <td className="py-1.5 text-right font-mono">{fmtDuration(r.avg_ms)}</td>
                          <td className="py-1.5 text-right font-mono">{fmtDuration(r.p95_ms)}</td>
                          <td className="py-1.5 text-right font-mono">{fmtDuration(r.max_ms)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </PageContainer>
  );
}
